import Tooltip from '@src/components/Tooltip'
import cx from 'clsx'
import React, { useMemo } from 'react'
import Select, { GroupBase, InputActionMeta, Props, SelectInstance, components } from 'react-select'
import Creatable from 'react-select/creatable'
import { twMerge } from 'tailwind-merge'

import { QuestionMarkCircleIcon } from '@heroicons/react/outline'
import { IconChevronDown, IconX } from '@tabler/icons-react'
import { getSelectStyles } from './utils'

export { useSelectHelpers } from './hooks/useSelectHelpers'

interface ReactSelectProps {
  label?: string | React.ReactElement
  errorMessage?: string
  tooltip?: string
  required?: boolean
  refObj?: React.RefObject<SelectInstance>
  classes?: {
    control?: string
    label?: string
    container?: string
    inputContainer?: string
    errorMessage?: string
  }
  isClearable?: boolean
  onCreateOption?: (value: string) => void
  handleInputChange?: (newValue: string, actionMeta: InputActionMeta) => void
  isValidNewOption?: (value: string) => boolean
}

export type Option = {
  label: string
  value: string
  [key: string]: unknown
}

export const ReactSelect = <
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  className,
  label,
  tooltip,
  errorMessage,
  required,
  classes,
  onCreateOption,
  handleInputChange,
  isValidNewOption,
  isClearable,
  refObj,
  components: selectComponents,
  ...props
}: Props<Option, IsMulti, Group> & ReactSelectProps) => {
  const styles = useMemo(() => getSelectStyles<Option, IsMulti, Group>(errorMessage), [errorMessage])

  const normalizedLabel = useMemo(() => {
    if (React.isValidElement(label) || typeof label === 'string' || typeof label === 'number') {
      return label
    }

    if (label && typeof label === 'object' && 'value' in label) {
      const value = (label as { value?: unknown }).value
      if (typeof value === 'string' || typeof value === 'number') {
        return value
      }
    }

    return ''
  }, [label])

  const tooltipId = `${props.name || props.id || 'select'}-${normalizedLabel}`
  const labelElement = normalizedLabel ? (
    <label className={cx('flex items-center mb-0.5 overflow-auto', classes?.label)}>
      {tooltip ? (
        <>
          <div data-tooltip-id={tooltipId} className="flex items-center">
            {normalizedLabel}
            <QuestionMarkCircleIcon className="h-3 ml-1" />
          </div>
          <Tooltip id={tooltipId}>
            <span>{tooltip}</span>
          </Tooltip>
        </>
      ) : (
        normalizedLabel
      )}
      {required && <span className="text-red-500">*</span>}
    </label>
  ) : null

  const Cmp = onCreateOption ? Creatable : Select

  const dataQa = `${label || props.name || props.id}-select`
  const customComponents = useMemo(
    () => ({
      ...selectComponents,
      Control: ({ children, ...propsControl }: Parameters<typeof components.Control<Option, IsMulti, Group>>[0]) => (
        <components.Control
          {...propsControl}
          innerProps={
            {
              'data-qa': dataQa,
              ...propsControl.innerProps,
            } as Record<string, unknown>
          }
        >
          {children}
        </components.Control>
      ),
      Option:
        selectComponents?.Option ||
        ((optionProps: Parameters<typeof components.Option<Option, IsMulti, Group>>[0]) => {
          return (
            <components.Option
              {...optionProps}
              innerProps={
                {
                  'data-qa': `${optionProps.label}-option`,
                  translate: 'no',
                  ...optionProps.innerProps,
                } as React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>
              }
            />
          )
        }),
      Menu: (menuProps: Parameters<typeof components.Menu<Option, IsMulti, Group>>[0]) => (
        <components.Menu
          {...menuProps}
          innerProps={
            {
              ...menuProps.innerProps,
              translate: 'no',
              className: cx((menuProps.innerProps as { className?: string } | undefined)?.className, 'notranslate'),
            } as React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>
          }
        />
      ),
      MenuPortal: (portalProps: Parameters<typeof components.MenuPortal<Option, IsMulti, Group>>[0]) => (
        <components.MenuPortal
          {...portalProps}
          innerProps={
            {
              ...portalProps.innerProps,
              translate: 'no',
              className: cx(
                (portalProps.innerProps as { className?: string } | undefined)?.className,
                'notranslate',
              ),
            } as React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>
          }
        />
      ),
      ClearIndicator: (indicatorProps: Parameters<typeof components.ClearIndicator<Option, IsMulti, Group>>[0]) => {
        return (
          components.ClearIndicator && (
            <components.ClearIndicator {...indicatorProps}>
              <IconX className="text-neutral-300" stroke={1.5} size={16} />
            </components.ClearIndicator>
          )
        )
      },
      DropdownIndicator: (indicatorProps: Parameters<typeof components.DropdownIndicator<Option, IsMulti, Group>>[0]) => {
        return (
          components.DropdownIndicator && (
            <components.DropdownIndicator {...indicatorProps}>
              <IconChevronDown className="text-neutral-300" stroke={1.5} size={16} />
            </components.DropdownIndicator>
          )
        )
      },
    }),
    [selectComponents, dataQa],
  )

  return (
    <div
      className={twMerge(cx(!errorMessage && 'mb-[1rem]', 'notranslate', className))}
      translate="no"
      onClick={(e) => e.stopPropagation()}
    >
      <div className={cx(classes?.container)}>
        {labelElement}
        <Cmp<Option, IsMulti, Group>
          ref={refObj as React.RefObject<SelectInstance<Option, IsMulti, Group>>}
          menuPortalTarget={document.body}
          components={customComponents}
          menuPlacement="auto"
          {...props}
          className={cx('rounded-base')}
          classNames={{
            container: () => cx(classes?.inputContainer),
            control: (propsControl) =>
              cx(classes?.control as string, propsControl.isDisabled ? '!bg-netural-50 !border-gray-300' : ''),
          }}
          onCreateOption={onCreateOption}
          onInputChange={handleInputChange}
          isValidNewOption={isValidNewOption}
          isClearable={isClearable}
          styles={{ ...styles, ...(props.styles || {}) }}
        />
      </div>
      {errorMessage && (
        <div className={cx('w-full mt-0.5 text-[11px] text-red-500', classes?.errorMessage)}>{errorMessage}</div>
      )}
    </div>
  )
}

export default ReactSelect
